import { axiosInstance } from "../App";
import { NomenclatorCodeDTO } from "../interface/interface";

type TerminologyItemDTO = {
  snomedid?: string | null;
  term?: string | null;
  snomedDescription?: string | null;
  cie10code?: string | null;
  cie10title?: string | null;
  cie10Description?: string | null;
};

type TerminologySearchResponseDTO = {
  items?: TerminologyItemDTO[] | null;
  hasNext?: boolean | null;
  page?: number | null;
  size?: number | null;
};

type SearchOptions = {
  context?: string;
  language?: string;
  offSet?: number;
  size?: number;
  preloadSize?: number;
  fallbackQuery?: string;
  minQueryLength?: number;
  throwOnRemoteError?: boolean;
};

type SearchPageOptions = {
  context?: string;
  language?: string;
  page?: number;
  size?: number;
  mappedToCie10Only?: boolean;
};

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  if (value && typeof value === "object") {
    return value as UnknownRecord;
  }
  return {};
}

export interface TerminologyApiErrorInfo {
  status: number | null;
  data: unknown;
  code: string | null;
  message: string;
  url: string | null;
  method: string | null;
  hasResponse: boolean;
}

export function getTerminologyApiErrorInfo(err: unknown): TerminologyApiErrorInfo {
  const error = asRecord(err);
  const response = asRecord(error.response);
  const config = asRecord(error.config);
  const parsedStatus = Number(response.status);
  const status = Number.isFinite(parsedStatus) ? parsedStatus : null;
  const data = response.data;
  const code = typeof error.code === "string" ? error.code : null;
  const message = typeof error.message === "string" ? error.message : "Unknown error";
  const url = typeof config.url === "string" ? config.url : null;
  const method = typeof config.method === "string" ? config.method.toUpperCase() : null;
  const hasResponse = status !== null || data !== undefined;

  return {
    status,
    data,
    code,
    message,
    url,
    method,
    hasResponse,
  };
}

export function getTerminologyApiErrorMessage(err: unknown, fallback: string): string {
  const details = getTerminologyApiErrorInfo(err);
  const data = asRecord(details.data);

  return (
    (typeof details.data === "string" ? details.data : undefined) ||
    (typeof data.message === "string" ? data.message : undefined) ||
    (typeof data.error === "string" ? data.error : undefined) ||
    (typeof data.detail === "string" ? data.detail : undefined) ||
    details.message ||
    fallback
  );
}

export type TerminologySnomedOption = NomenclatorCodeDTO & {
  cie10code?: string;
  cie10title?: string;
  cie10Description?: string;
};

export type TerminologySnomedPageResult = {
  items: TerminologySnomedOption[];
  hasNext: boolean;
  page: number;
  size: number;
};

const DEFAULT_SEED_QUERIES_BY_CONTEXT: Record<string, string[]> = {
  DIAGNOSIS: ["dolor", "fiebre", "tos", "infeccion"],
  PROCEDURE: ["estudio", "radiografia", "ecografia", "laboratorio"],
  GENERAL: ["consulta", "clinico", "procedimiento", "diagnostico"],
};
const DEFAULT_REMOTE_PAGE_SIZE = 20;
const DEFAULT_PRELOAD_SIZE = 20;
const terminologyCacheByContext = new Map<string, Map<string, TerminologySnomedOption>>();
const preloadPromiseByContext = new Map<string, Promise<TerminologySnomedOption[]>>();

function getContextKey(context = "GENERAL"): string {
  return normalize(context || "GENERAL").toUpperCase() || "GENERAL";
}

function getContextCache(context = "GENERAL"): Map<string, TerminologySnomedOption> {
  const key = getContextKey(context);
  if (!terminologyCacheByContext.has(key)) {
    terminologyCacheByContext.set(key, new Map<string, TerminologySnomedOption>());
  }
  return terminologyCacheByContext.get(key)!;
}

function cacheKeyOf(item: Pick<NomenclatorCodeDTO, "code" | "description">): string {
  return `${item.code ?? ""}||${item.description ?? ""}`.trim();
}

function mergeIntoCache(items: TerminologySnomedOption[], context = "GENERAL"): TerminologySnomedOption[] {
  const cache = getContextCache(context);
  items.forEach((item) => {
    const key = cacheKeyOf(item);
    if (key) {
      cache.set(key, item);
    }
  });
  return Array.from(cache.values());
}

function normalize(value = ""): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function mapResponseItems(data: TerminologySearchResponseDTO | undefined | null): TerminologySnomedOption[] {
  const items = Array.isArray(data?.items) ? data.items : [];
  return items
    .map((item) => {
      const code = item?.snomedid?.trim();
      const description = item?.snomedDescription?.trim() || item?.term?.trim() || "";
      if (!code || !description) {
        return null;
      }

      return {
        id: Number(code),
        code,
        description,
        categorydescription: "SNOMED CT",
        cie10code: item?.cie10code?.trim() || undefined,
        cie10title: item?.cie10title?.trim() || undefined,
        cie10Description: item?.cie10Description?.trim() || undefined,
      } as TerminologySnomedOption;
    })
    .filter((item): item is TerminologySnomedOption => item !== null);
}

async function fetchSnomedSearch(
  query: string,
  {
    context = "PROCEDURE",
    language = "es",
    offSet = 0,
    size = DEFAULT_REMOTE_PAGE_SIZE,
  }: SearchOptions = {}
): Promise<TerminologySnomedOption[]> {
  const page = Math.floor(Math.max(offSet, 0) / Math.max(size, 1));
  const { data } = await axiosInstance.get<TerminologySearchResponseDTO>(
    "/api/secured/terminology/snomed",
    {
      params: {
        query,
        context,
        language,
        page,
        size,
      },
    }
  );

  return mapResponseItems(data);
}

function filterCachedStudies(query: string, context = "GENERAL"): TerminologySnomedOption[] {
  const normalizedQuery = normalize(query);
  const cached = Array.from(getContextCache(context).values());

  if (!normalizedQuery) {
    return cached;
  }

  return cached
    .filter((item) => {
      const code = normalize(item.code ?? "");
      const description = normalize(item.description ?? "");
      return code.includes(normalizedQuery) || description.includes(normalizedQuery);
    });
}

function slicePage(items: TerminologySnomedOption[], offSet = 0, pageSize = DEFAULT_REMOTE_PAGE_SIZE): TerminologySnomedOption[] {
  return items.slice(offSet, offSet + pageSize);
}

async function ensurePreloadedStudies(options: SearchOptions = {}): Promise<TerminologySnomedOption[]> {
  const contextKey = getContextKey(options.context);
  const cache = getContextCache(contextKey);
  if (cache.size > 0) {
    return Array.from(cache.values());
  }

  if (!preloadPromiseByContext.has(contextKey)) {
    const seedQueries = DEFAULT_SEED_QUERIES_BY_CONTEXT[contextKey] ?? DEFAULT_SEED_QUERIES_BY_CONTEXT.GENERAL;
    const preloadPromise = (async () => {
      const settled = await Promise.allSettled(
        seedQueries.map((query) =>
          fetchSnomedSearch(query, {
            ...options,
            size: options.preloadSize ?? DEFAULT_PRELOAD_SIZE,
          })
        )
      );

      const merged = settled
        .filter((result): result is PromiseFulfilledResult<TerminologySnomedOption[]> => result.status === "fulfilled")
        .flatMap((result) => result.value);

      return mergeIntoCache(merged, contextKey);
    })().finally(() => {
      preloadPromiseByContext.delete(contextKey);
    });
    preloadPromiseByContext.set(contextKey, preloadPromise);
  }

  return preloadPromiseByContext.get(contextKey)!;
}

async function fetchWithRetry(query: string, options: SearchOptions, attempts = 2): Promise<TerminologySnomedOption[]> {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const items = await fetchSnomedSearch(query, options);
      mergeIntoCache(items, options.context);
      return items;
    } catch (error) {
      lastError = error;
      if (attempt >= attempts) {
        break;
      }
    }
  }
  throw lastError;
}

export async function searchTerminologySnomed(
  query: string,
  {
    context = "PROCEDURE",
    language = "es",
    offSet = 0,
    size = DEFAULT_REMOTE_PAGE_SIZE,
    preloadSize = DEFAULT_PRELOAD_SIZE,
    fallbackQuery = "estudio",
    minQueryLength = 3,
    throwOnRemoteError = false,
  }: SearchOptions = {}
): Promise<TerminologySnomedOption[]> {
  const normalizedQuery = query.trim();
  const page = Math.floor(Math.max(offSet, 0) / Math.max(size, 1));
  const options = { context, language, offSet, size, preloadSize, fallbackQuery, minQueryLength };
  const remoteOptions = { ...options, offSet: page * size };

  await ensurePreloadedStudies(options);

  if (normalizedQuery.length < minQueryLength) {
    const localResults = slicePage(filterCachedStudies(normalizedQuery, context), offSet, preloadSize);
    if (localResults.length > 0) {
      return localResults;
    }

    try {
      const fallbackResults = await fetchWithRetry(fallbackQuery, remoteOptions);
      return fallbackResults.slice(0, preloadSize);
    } catch (error) {
      const cachedResults = slicePage(filterCachedStudies("", context), offSet, preloadSize);
      if (cachedResults.length > 0) {
        return cachedResults;
      }
      if (throwOnRemoteError) {
        throw error;
      }
      return cachedResults;
    }
  }

  try {
    return await fetchWithRetry(normalizedQuery, remoteOptions);
  } catch (error) {
    const localResults = slicePage(filterCachedStudies(normalizedQuery, context), offSet, size);
    if (localResults.length > 0) {
      return localResults;
    }
    const cachedResults = slicePage(filterCachedStudies("", context), offSet, size);
    if (cachedResults.length > 0) {
      return cachedResults;
    }
    if (throwOnRemoteError) {
      throw error;
    }
    return cachedResults;
  }
}

export async function searchTerminologySnomedPage(
  query: string,
  {
    context = "DIAGNOSIS",
    language = "es",
    page = 0,
    size = DEFAULT_REMOTE_PAGE_SIZE,
    mappedToCie10Only = false,
  }: SearchPageOptions = {}
): Promise<TerminologySnomedPageResult> {
  const { data } = await axiosInstance.get<TerminologySearchResponseDTO>(
    "/api/secured/terminology/snomed",
    {
      params: {
        query,
        context,
        language,
        page,
        size,
        mappedToCie10Only,
      },
    }
  );

  return {
    items: mapResponseItems(data),
    hasNext: Boolean(data?.hasNext),
    page: Number.isFinite(Number(data?.page)) ? Number(data?.page) : page,
    size: Number.isFinite(Number(data?.size)) ? Number(data?.size) : size,
  };
}

export async function getTerminologyCie10FromSnomed(conceptId: string, language = "es") {
  const { data } = await axiosInstance.get<TerminologyItemDTO[]>(
    `/api/secured/terminology/snomed/${conceptId}/cie10`,
    {
      params: { language },
    }
  );
  return Array.isArray(data) ? data : [];
}

export async function getTerminologySnomedFromCie10(code: string, language = "es") {
  const { data } = await axiosInstance.get<TerminologySearchResponseDTO>(
    `/api/secured/terminology/cie10/${encodeURIComponent(code)}/snomed`,
    {
      params: { language, page: 0, size: 20 },
    }
  );
  return mapResponseItems(data);
}
